import { assertWriteLanded } from "@/lib/scheduledTaskEdit";

/**
 * Issue #1214 — removing a kid left her stored facts live, so Kin went on
 * treating a removed child as part of the family.
 *
 * `MyKidsSection.handleRemove` took the name out of
 * `user_preferences.known_kid_names` (that half worked) and then archived her
 * `family_context` rows with `.eq("subject", name)` — a **case-sensitive**
 * match against a column nothing case-normalizes on write. kin-chat stores
 * `subject` trimmed and otherwise untouched.
 *
 * Confirmed in production on kdchambers3@gmail.com:
 *
 *     kid, sarah, diaper_size, 4, pro_onboarding, archived=FALSE, 2026-07-11
 *     known_kid_names = ["C","J"]        <- Sarah correctly gone
 *
 * Lowercase `sarah`, so `.eq("subject","Sarah")` matched nothing and the row
 * survived. #48 ("Stop silently lowercasing kid names on save") closed
 * 2026-07-12 13:41 UTC; that row was written the day before, under the old
 * regime. Every account that stored kid facts before that date and removed or
 * renamed the kid after is exposed the same way.
 *
 * ── Why match in JS rather than with ilike ───────────────────────────────────
 *
 * `.ilike("subject", name)` is the obvious pushdown and it is a trap this repo
 * has already been bitten by: `%` and `_` are LIKE wildcards, so a name
 * containing either matches rows it should not. `mealFavorites.ts` carries the
 * same warning and deletes by id for exactly this reason, and
 * `escapeIlikeWildcards` exists precisely because a Copilot review caught an
 * unescaped call in kin-chat.
 *
 * That helper would work here, but it lives under `supabase/functions/kin-chat/`
 * — a locked zone — and importing it would pull that file into the app bundle.
 * Exact `trim().toLowerCase()` equality is not a weaker match than an escaped
 * `ilike`; it is a stronger one, with no pattern semantics to get wrong. The
 * row volume is one family's kid facts.
 *
 * ── Why every function here reads its write back ─────────────────────────────
 *
 * House rule 16. An RLS-refused UPDATE returns 200 with `error === null` and
 * zero rows, so the old code's `else` branch logged
 * `archived facts for removed kid: Sarah` over a write that changed nothing.
 *
 * But **zero rows is also the legitimate answer** when a kid simply has no
 * stored facts — which is why these functions SELECT first and only assert the
 * write landed when there was something to write. `assertWriteLanded` on a bare
 * update would throw on the ordinary "nothing to archive" case.
 */

/** Row shape these helpers read back. */
interface IdRow {
  id: string;
}

/**
 * The minimal slice of the Supabase client these helpers use.
 *
 * Structural rather than the imported client type so the unit tests can pass a
 * fake and assert on the exact filters issued — the filters ARE the bug.
 */
/**
 * ⚠️ NO KID NAME IN A THROWN ERROR MESSAGE.
 *
 * MyKidsSection catches these and logs them with console.error, and Sentry's
 * captureConsoleIntegration is configured at levels ["error"] (main.tsx) — so
 * that console line becomes a Sentry event on its own, with no explicit
 * captureException anywhere in the path. A removed child's name would ship to a
 * third-party log from the very action that is supposed to erase her from Kin's
 * view. Her ledger row and her facts are deleted with her account; a Sentry
 * event is not. (#1243; the throws predate it, from #1214.)
 *
 * ⚠️ KEEP THE COUNTS. "Archived 3 of 5" is the useful half — a partial archive
 * is exactly the case where something was left behind and she needs to be told.
 * Drop the name, never the numbers.
 *
 * The user-facing toasts still name her, correctly: the caller has the name in
 * scope and she is looking at the screen she typed it into.
 */
export interface KidFactClient {
  from(table: string): any;
}

/** Case- and whitespace-insensitive name equality. The one comparison rule. */
export function sameKidName(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();
}

/** Every live `category='kid'` fact for this user, with its subject. */
async function liveKidFacts(
  client: KidFactClient,
  userId: string,
): Promise<Array<{ id: string; subject: string | null }>> {
  const { data, error } = await client
    .from("family_context")
    .select("id, subject")
    .eq("user_id", userId)
    .eq("category", "kid")
    .eq("archived", false);
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

/**
 * Archive every stored fact about a removed kid, matching the name
 * case-insensitively.
 *
 * Returns how many rows were archived. Zero is a valid result (she had no
 * facts stored); a refused write throws rather than reporting success.
 */
export async function archiveFactsForKid(
  client: KidFactClient,
  userId: string,
  name: string,
): Promise<{ archived: number }> {
  const ids = (await liveKidFacts(client, userId))
    .filter((row) => sameKidName(row.subject, name))
    .map((row) => row.id);
  if (ids.length === 0) return { archived: 0 };

  const { data, error } = await client
    .from("family_context")
    .update({ archived: true })
    .in("id", ids)
    .eq("user_id", userId)
    .select("id");
  if (error) throw error;
  // We know rows matched, so zero back means the write was refused.
  assertWriteLanded(data, "kid facts");
  const landed = (data as IdRow[]).length;
  if (landed < ids.length) {
    throw new Error(
      `Archived ${landed} of ${ids.length} facts — the rest are unchanged.`,
    );
  }
  return { archived: landed };
}

/**
 * Move every fact from one kid name to another, matching case-insensitively.
 *
 * The old code's `.eq("subject", oldName)` left differently-cased rows stranded
 * under the previous name, where nothing reads them and removal cannot find
 * them either.
 */
export async function renameFactSubjectForKid(
  client: KidFactClient,
  userId: string,
  oldName: string,
  newName: string,
): Promise<{ renamed: number }> {
  const ids = (await liveKidFacts(client, userId))
    .filter((row) => sameKidName(row.subject, oldName))
    .map((row) => row.id);
  if (ids.length === 0) return { renamed: 0 };

  const { data, error } = await client
    .from("family_context")
    .update({ subject: newName })
    .in("id", ids)
    .eq("user_id", userId)
    .select("id");
  if (error) throw error;
  assertWriteLanded(data, "kid facts");
  const landed = (data as IdRow[]).length;
  if (landed < ids.length) {
    throw new Error(
      `Renamed ${landed} of ${ids.length} facts — the rest are unchanged.`,
    );
  }
  return { renamed: landed };
}

/**
 * Clear `family_providers.linked_kid_name` for a removed kid.
 *
 * Providers themselves are household records and are NOT archived — a
 * pediatrician stays a pediatrician. Only the link to the removed child goes,
 * so her name stops appearing on a provider card.
 */
export async function unlinkProvidersForKid(
  client: KidFactClient,
  userId: string,
  name: string,
): Promise<{ unlinked: number }> {
  const { data: rows, error: findErr } = await client
    .from("family_providers")
    .select("id, linked_kid_name")
    .eq("user_id", userId)
    .eq("archived", false);
  if (findErr) throw findErr;

  const ids = (Array.isArray(rows) ? rows : [])
    .filter((row: { linked_kid_name: string | null }) => sameKidName(row.linked_kid_name, name))
    .map((row: IdRow) => row.id);
  if (ids.length === 0) return { unlinked: 0 };

  const { data, error } = await client
    .from("family_providers")
    .update({ linked_kid_name: null })
    .in("id", ids)
    .eq("user_id", userId)
    .select("id");
  if (error) throw error;
  assertWriteLanded(data, "provider links");
  return { unlinked: (data as IdRow[]).length };
}
